import { MarkdownParser } from '@repo/lib/markdown';
import { ImagePlusIcon, TrashIcon, TypeIcon } from 'lucide-react';
import { useState } from 'react';
import { useWatch } from 'react-hook-form';

import { AnswerCorrectToggle, AnswerExplanationDialog, AnswerExplanationSection, AnswerMediaSelectDialog } from '@/components/features/quiz/question/question-builder';
import { Button, ButtonGroup } from '@/components/ui/button';
import { ErrorMessage, FormControl, FormField, FormItem } from '@/components/ui/form';
import { Markdown } from '@/components/ui/markdown';
import { Textarea } from '@/components/ui/textarea';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';

import type { QuestionSingleAnswerFieldProps } from '.';

export function QuestionSingleAnswerField(questionSingleAnswerFieldProps: QuestionSingleAnswerFieldProps) {
  const { form, index, toggleCorrect, removeAnswer, canRemove } = questionSingleAnswerFieldProps;
  const { control } = form;
  const [openExplanation, setOpenExplanation] = useState<boolean>(false);
  const [openMediaDialog, setOpenMediaDialog] = useState<boolean>(false);

  const removeExplanation = () => {
    form.setValue(`answers.${index}.explanation`, undefined);
  };

  const handleToggleType = () => {
    if (isMediaType) {
      form.setValue(`answers.${index}.content`, '');
    } else {
      setOpenMediaDialog(true);
    }
  };

  const explanation: string | undefined = useWatch({
    control: control,
    name: `answers.${index}.explanation`,
  });
  const answerValue: string = useWatch({
    control: control,
    name: `answers.${index}.content`,
  });

  const isMediaType: boolean = MarkdownParser.hasElementTypes(answerValue);

  return (
    <div className="bg-card flex w-full flex-col rounded-xl border shadow-sm">
      <AnswerMediaSelectDialog
        open={openMediaDialog}
        onOpenChange={setOpenMediaDialog}
        contentChange={(content: string) => {
          form.setValue(`answers.${index}.content`, content);
        }}
      />
      <div className="flex justify-between gap-3 border-b p-2">
        <ButtonGroup>
          <Tooltip>
            <TooltipTrigger asChild>
              <Button variant="outline" size="sm" type="button" onClick={handleToggleType}>
                {isMediaType ? <TypeIcon /> : <ImagePlusIcon />}
              </Button>
            </TooltipTrigger>
            <TooltipContent>{isMediaType ? 'Przełącz na tekst' : 'Przełącz na media'}</TooltipContent>
          </Tooltip>
          <Tooltip>
            <TooltipTrigger asChild>
              <Button variant="outline" size="sm" disabled={!canRemove} type="button" onClick={() => removeAnswer(index)}>
                <TrashIcon />
              </Button>
            </TooltipTrigger>
            <TooltipContent>Usuń odpowiedź</TooltipContent>
          </Tooltip>
        </ButtonGroup>
        <FormField
          name={`answers.${index}.correct`}
          render={({ field }) => (
            <FormItem className="w-fit">
              <FormControl>
                <AnswerCorrectToggle {...field} correct={field.value} onCorrectChange={() => toggleCorrect(index)} />
              </FormControl>
            </FormItem>
          )}
        />
      </div>
      <div className="p-2">
        <FormField
          name={`answers.${index}.content`}
          render={({ field }) => (
            <FormItem className="h-full grow">
              <FormControl>
                {isMediaType ? (
                  <div className="bg-accent/25 relative flex max-h-35 justify-center rounded-md">
                    <Markdown content={field.value} videoClassName="rounded-md h-full" imageClassName="rounded-md h-full" />
                  </div>
                ) : (
                  <Textarea {...field} className="shadnow-none min-h-35 resize-none border-none" placeholder="Wprowadź odpowiedź" />
                )}
              </FormControl>
            </FormItem>
          )}
        />
      </div>
      <div className="flex flex-col gap-2 p-2">
        <AnswerExplanationSection explanation={explanation} onOpenExplanation={() => setOpenExplanation(true)} />
        <FormField
          name={`answers.${index}.explanation`}
          render={({ field }) => (
            <AnswerExplanationDialog
              open={openExplanation}
              onOpenChange={setOpenExplanation}
              onRemoveExplanation={removeExplanation}
              value={field.value}
              onValueChange={(value) => field.onChange(value)}
            />
          )}
        />
        <ErrorMessage name={`answers.${index}.content`} className="min-h-0 empty:hidden" />
      </div>
    </div>
  );
}
